import { useForm } from '@tanstack/react-form';
import { Button, Card, Input, Text } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../../context/page-context';
import { SettingsPasswordError } from '../errors';
import { useUpdatePassword } from '../hooks/use-update-password';
import { type UpdatePasswordFormState, updatePasswordFormSchema } from '../schemas';

export function SettingsPasswordForm() {
  const { notificationHandler } = useHandlers();
  const updatePassword = useUpdatePassword();

  const form = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    } satisfies UpdatePasswordFormState,
    validators: {
      onSubmit: updatePasswordFormSchema,
    },
    onSubmit: ({ value }) => {
      const payload = updatePasswordFormSchema.parse(value);

      updatePassword.mutate(payload, {
        onSuccess: () => {
          notificationHandler.success(
            t('features:account.components.settings_password_form.notifications.success'),
          );
        },
        onError: (error) => {
          if (error instanceof SettingsPasswordError && error.id === 'no_match') {
            notificationHandler.error(
              t('features:account.components.settings_password_form.notifications.no_match'),
            );

            // Sets error on the currentPassword field
            form.setFieldMeta('currentPassword', (meta) => ({
              ...meta,
              errorMap: {
                ...meta.errorMap,
                onSubmit: {
                  message: t(
                    'features:account.components.settings_password_form.notifications.no_match',
                  ),
                },
              },
            }));

            return;
          }

          notificationHandler.error(
            t('features:account.components.settings_password_form.notifications.update_failed'),
          );
        },
      });
    },
    onSubmitInvalid: () =>
      notificationHandler.error(
        t('features:account.components.settings_password_form.notifications.update_invalid'),
      ),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();

        // Clear errors so we can re-validate
        form.setErrorMap({ onSubmit: { fields: {} } });

        form.handleSubmit();
      }}
    >
      <Card>
        <Card.Header headingLevel="h2">
          {t('features:account.components.settings_password_form.title')}
        </Card.Header>
        <Card.Body>
          <div className="flex flex-col gap-4">
            <Text variant="secondary">
              {t('features:account.components.settings_password_form.description')}
            </Text>
            <div className="flex flex-col gap-4">
              <div className="grid w-full gap-4 sm:grid-cols-2">
                <form.Field name="currentPassword">
                  {(field) => (
                    <Input
                      label={t(
                        'features:account.components.settings_password_form.details.current_password.label',
                      )}
                      placeholder="••••••••••••••••"
                      type="password"
                      value={field.state.value}
                      onChange={field.handleChange}
                      error={field.state.meta.errors[0]?.message}
                    />
                  )}
                </form.Field>
              </div>
              <div className="grid w-full gap-4 sm:grid-cols-2">
                <form.Field name="newPassword">
                  {(field) => (
                    <Input
                      label={t(
                        'features:account.components.settings_password_form.details.new_password.label',
                      )}
                      placeholder="••••••••••••••••"
                      type="password"
                      value={field.state.value}
                      onChange={field.handleChange}
                      minLength={12}
                      maxLength={128}
                      error={field.state.meta.errors[0]?.message}
                    />
                  )}
                </form.Field>
                <form.Field name="confirmNewPassword">
                  {(field) => (
                    <Input
                      label={t(
                        'features:account.components.settings_password_form.details.confirm_new_password.label',
                      )}
                      placeholder="••••••••••••••••"
                      type="password"
                      value={field.state.value}
                      onChange={field.handleChange}
                      minLength={12}
                      maxLength={128}
                      error={field.state.meta.errors[0]?.message}
                    />
                  )}
                </form.Field>
              </div>
            </div>
            <Button type="submit" loading={updatePassword.isPending}>
              {t('features:account.components.settings_password_form.action')}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </form>
  );
}
